import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { consultationService } from '../services/consultationService'
import { ConsultationArtefactType } from '../types/consultation'
import './CreateArtefactForm.css'

interface CreateArtefactFormProps {
  consultationId: string
  onSuccess?: () => void
  onCancel?: () => void
}

export function CreateArtefactForm({ consultationId, onSuccess, onCancel }: CreateArtefactFormProps) {
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState({
    type: 'referral-letter' as ConsultationArtefactType,
    title: '',
    description: '',
    content: '',
    referenceNumber: '',
    status: 'draft' as const,
  })

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) =>
      consultationService.addArtefact(consultationId, {
        ...data,
        createdBy: undefined, // Will be set by backend
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultation', consultationId] })
      queryClient.invalidateQueries({ queryKey: ['consultations'] })
      onSuccess?.()
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.title.trim()) {
      alert('Please enter a title')
      return
    }

    try {
      await createMutation.mutateAsync(formData)
    } catch (error: any) {
      alert(error?.message || 'Failed to create artefact')
    }
  }

  const artefactTypeOptions: { value: ConsultationArtefactType; label: string; icon: string }[] = [
    { value: 'referral-letter', label: 'Referral Letter', icon: '📄' },
    { value: 'prescription', label: 'Prescription', icon: '💊' },
    { value: 'lab-order', label: 'Lab Order', icon: '🧪' },
    { value: 'imaging-order', label: 'Imaging Order', icon: '📷' },
    { value: 'certificate', label: 'Certificate', icon: '📜' },
    { value: 'other', label: 'Other', icon: '📋' },
  ]

  return (
    <form className="create-artefact-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor="type">
          Artefact Type <span className="required">*</span>
        </label>
        <select
          id="type"
          value={formData.type}
          onChange={(e) => setFormData({ ...formData, type: e.target.value as ConsultationArtefactType })}
          required
        >
          {artefactTypeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.icon} {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="title">
          Title <span className="required">*</span>
        </label>
        <input
          id="title"
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="e.g., Cardiology Referral, Prescription for..."
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="description">Description</label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={3}
          placeholder="Brief description of the artefact..."
        />
      </div>

      {(formData.type === 'referral-letter' || formData.type === 'certificate' || formData.type === 'other') && (
        <div className="form-group">
          <label htmlFor="content">Content / Letter Body</label>
          <textarea
            id="content"
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            rows={12}
            placeholder="Enter the full content of the letter, certificate, or document..."
            className="content-editor"
          />
        </div>
      )}

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="referenceNumber">Reference Number</label>
          <input
            id="referenceNumber"
            type="text"
            value={formData.referenceNumber}
            onChange={(e) => setFormData({ ...formData, referenceNumber: e.target.value })}
            placeholder="e.g., REF-12345, RX-67890"
          />
        </div>

        <div className="form-group">
          <label htmlFor="status">Status</label>
          <select
            id="status"
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
          >
            <option value="draft">Draft</option>
            <option value="issued">Issued</option>
            <option value="sent">Sent</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </div>

      <div className="form-actions">
        {onCancel && (
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Cancel
          </button>
        )}
        <button type="submit" className="btn-primary" disabled={createMutation.isPending}>
          {createMutation.isPending ? 'Creating...' : 'Create Artefact'}
        </button>
      </div>
    </form>
  )
}

































